import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, User, Menu, Heart } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCart, useAuth } from "@/lib/store";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import SearchOverlay from "@/components/SearchOverlay";
import LanguageSelector from "@/components/LanguageSelector";

export default function Navbar() {
  const { t } = useTranslation();
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/shop",                        label: t("nav.all") },
    { to: "/shop?category=new",           label: t("nav.new_arrivals") },
    { to: "/shop?category=arab",          label: t("nav.arab") },
    { to: "/shop?gender=femenino",        label: t("nav.women") },
    { to: "/shop?gender=masculino",       label: t("nav.men") },
    { to: "/shop?category=kids",          label: t("nav.kids") },
    { to: "/shop?category=gift_set",      label: t("nav.gift_sets") },
  ];

  return (
    <>
      {/* Trust marquee */}
      <div className="bg-navy text-cream/90 border-b border-white/10 overflow-hidden" data-testid="trust-marquee">
        <div className="flex items-center justify-between px-6 lg:px-12">
          <div className="flex-1 flex whitespace-nowrap marquee-track font-mono-caps py-2 gap-16">
            {Array(3).fill(0).map((_, i) => (
              <div key={i} className="flex gap-16 flex-shrink-0">
                <span>{t("footer.originality")}</span>
                <span>·</span>
                <span>{t("footer.shipping_returns")}</span>
                <span>·</span>
                <span>{t("footer.returns")}</span>
                <span>·</span>
              </div>
            ))}
          </div>
          <div className="hidden sm:block font-mono-caps text-xs pl-6 py-2 border-l border-cream/20 ml-4">
            <LanguageSelector variant="topbar" />
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/90 border-b border-border" data-testid="navbar">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 lg:px-12 h-20">
          <Sheet>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 -ml-2" data-testid="mobile-menu-btn" aria-label={t("nav.boutique")}>
                <Menu strokeWidth={1.25} className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[85vw] sm:w-[400px] rounded-none border-r">
              <div className="mt-10 flex flex-col gap-6">
                {links.map((l) => (
                  <Link key={l.to} to={l.to} className="text-2xl font-serif" data-testid={`mobile-nav-${l.label}`}>
                    {l.label}
                  </Link>
                ))}
                <div className="pt-6 border-t"><LanguageSelector variant="menu" /></div>
              </div>
            </SheetContent>
          </Sheet>

          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src="/brand/logo.png" alt="Pure Fragance" className="h-12 w-12 object-cover rounded-full" />
            <span className="tracking-[0.3em] text-base font-serif hidden sm:inline">PURE FRAGANCE</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8 font-mono-caps text-xs text-foreground/80" data-testid="desktop-nav">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} className={({ isActive }) => `transition-colors hover:text-foreground ${isActive ? "text-foreground" : ""}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button onClick={() => setSearchOpen(true)} className="p-3 hover:text-navy transition-colors" aria-label={t("nav.search")} data-testid="nav-search-btn">
              <Search strokeWidth={1.25} className="h-5 w-5" />
            </button>
            <Link to="/account/wishlist" className="p-3 hover:text-navy transition-colors hidden sm:inline-flex" data-testid="nav-wishlist-btn" aria-label={t("account.wishlist")}>
              <Heart strokeWidth={1.25} className="h-5 w-5" />
            </Link>
            <button onClick={() => user ? navigate("/account") : navigate("/login")} className="p-3 hover:text-navy transition-colors" aria-label={t("nav.account")} data-testid="nav-account-btn">
              <User strokeWidth={1.25} className="h-5 w-5" />
            </button>
            <button onClick={() => setOpen(true)} className="p-3 relative hover:text-navy transition-colors" aria-label={t("nav.cart")} data-testid="nav-cart-btn">
              <ShoppingBag strokeWidth={1.25} className="h-5 w-5" />
              {count > 0 && (
                <span className="absolute top-1 right-0 bg-gold text-black text-[10px] font-medium w-4 h-4 flex items-center justify-center" data-testid="cart-count">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>
      <SearchOverlay open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}
